/**
 * Closes the gap where the sticky header is neither pinned nor on screen.
 *
 * Impulse pins the header on a single hard threshold — `window.scrollY > 250`, in theme.js's
 * `scrollHandler` — with no hysteresis. Below that it drops the header back into normal flow at
 * the top of the document, so between the header leaving the viewport and 250px it is simply
 * gone. Measured on /collections/strength at 390x844: invisible from scrollY 115 through 250 in
 * both directions.
 *
 * The threshold lives in a closure and the module exposes only `init` and `disableOverlayHeader`,
 * so there is nothing to configure. theme.js is vendor code and is not edited. This asserts
 * Impulse's own classes across the band instead, so styling and the transition stay in one place.
 *
 * Both header classes are required: `--stuck` alone parks the header at `top: -60px`, its
 * pre-animation position, and Impulse adds `--opening` 100ms later to slide it in.
 */
const initHeaderPeek = () => {
  const wrapper = document.querySelector('#HeaderWrapper');
  const header = document.querySelector('#SiteHeader');
  const spacer = document.getElementById('StickyHeaderWrap');

  // No spacer means Impulse never enabled sticky here (data-sticky="false"), so there is no band
  // to close — pinning without the spacer reserving height would shift the page.
  if (!header || !spacer) return null;

  const PINNED = ['site-header--stuck', 'site-header--opening'];
  const OVERLAY = 'is-light';

  // Impulse only manages the overlay class on pages that started overlayed, so mirror that test
  // rather than adding the class to a page that never had it.
  const isOverlayPage = Boolean(wrapper && wrapper.classList.contains(OVERLAY));

  // The top of what the shopper can actually see. Usually 0, but the announcement bar can be
  // sticky above the header (`sticky_on_scroll`, desktop-only on this store), in which case it
  // occludes everything behind it and the header is gone well before it reaches 0. Impulse marks
  // that state on the body: the plain class means the bar sits above the header, `--below` means
  // it sits under it and occludes nothing.
  const visibleTop = () => {
    if (!document.body.classList.contains('has-sticky-announcement-bar')) return 0;
    const bar = document.querySelector('.announcement-bar');
    if (!bar) return 0;
    const rect = bar.getBoundingClientRect();
    // Only once it is actually pinned at the top — before that it scrolls with the page.
    return rect.top <= 0 ? Math.max(0, rect.bottom) : 0;
  };

  // Pin the moment the in-flow header has actually left the visible area, not when scrollY passes
  // its height: the header sits below the announcement bar, so its own height understates how far
  // it travels. Measuring the spacer against the visible top means the fixed position is taken up
  // exactly where the static one was surrendered, with no jump and nothing lost behind the bar.
  const hasLeftViewport = () => spacer.getBoundingClientRect().bottom <= visibleTop();

  const controller = new AbortController();
  const { signal } = controller;

  // Guarded so the observer does not re-enter on its own writes.
  let applying = false;
  const apply = () => {
    if (applying) return;
    applying = true;
    const pin = hasLeftViewport();
    if (pin) header.classList.add(...PINNED);
    else header.classList.remove(...PINNED);
    // Impulse drops the overlay class whenever the header is pinned, because the transparent
    // treatment (inverted logo, white nav) is only legible over the hero. Left in place through
    // this band it would sit on the pinned header's solid background.
    if (isOverlayPage) wrapper.classList.toggle(OVERLAY, !pin);
    applying = false;
  };

  // Deferred a frame rather than run inline. The threshold reads the announcement bar's live rect,
  // but Impulse toggles `announcement-bar--sticky` inside its own scroll rAF — so measuring
  // synchronously can catch the bar mid-move, decide not to pin, and leave nothing to re-run once
  // it settles. A rAF puts this after Impulse's callback, when the rect is final.
  let frame = null;
  const scheduleApply = () => {
    if (frame !== null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      apply();
    });
  };

  window.addEventListener('scroll', scheduleApply, { passive: true, signal });
  window.addEventListener('resize', scheduleApply, { passive: true, signal });

  // The mobile nav animates the announcement bar until `bfx-mobile-nav-settling` clears, which is
  // a layout change no scroll event follows. Without this the header can be left unpinned against
  // a bar that has since moved.
  const settleObserver = new MutationObserver(scheduleApply);
  settleObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

  // Impulse's handler is debounced 30ms, so it gets the last word at its own 250 boundary and
  // strips both classes once on the way down. Re-asserting in the same microtask avoids the
  // ~30ms flash a longer debounce here would leave.
  const observer = new MutationObserver(() => {
    if (hasLeftViewport() && !header.classList.contains('site-header--stuck')) apply();
  });
  observer.observe(header, { attributes: true, attributeFilter: ['class'] });

  apply();

  return () => {
    controller.abort();
    observer.disconnect();
    settleObserver.disconnect();
    if (frame !== null) cancelAnimationFrame(frame);
  };
};

// The theme editor replaces the header section — and with it #SiteHeader and #StickyHeaderWrap —
// so listeners bound to the old nodes would leave the band open again after any header setting
// change. Tear down and rebind against the replacements.
let teardownHeaderPeek = initHeaderPeek();

document.addEventListener('shopify:section:load', (event) => {
  if (!event.target.querySelector('#SiteHeader')) return;
  if (teardownHeaderPeek) teardownHeaderPeek();
  teardownHeaderPeek = initHeaderPeek();
});
